import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Avatar
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import html2pdf from 'html2pdf.js';
import { formatTranscriptTimestamp } from '../utils';

const parseSRT = (srtText) => {
  let blocks = srtText.trim().split(/\r?\n\s*\r?\n/).filter((b) => b.trim() !== '');
  if (blocks.length <= 1) {
    const lines = srtText.trim().split(/\r?\n/);
    blocks = [];
    for (let i = 0; i < lines.length;) {
      if (i + 2 < lines.length) {
        blocks.push(lines[i] + '\n' + lines[i + 1] + '\n' + lines[i + 2]);
        i += 3;
        if (lines[i] && lines[i].trim() === '') i++;
      } else break;
    }
  }
  return blocks
    .map((block, idx) => {
      const lines = block.split(/\r?\n/);
      if (lines.length < 3) return null;
      const [, timeLine, ...textLines] = lines;
      const [startStr, endStr] = timeLine.split(' --> ');
      const parseTime = (t) => {
        const p = t.split(/[:,]/).map((n) => parseInt(n, 10));
        return p[0] * 3600 + p[1] * 60 + p[2] + p[3] / 1000;
      };
      return {
        start: parseTime(startStr.trim()),
        end: parseTime(endStr.trim()),
        text: textLines.join(' ').trim(),
        index: idx
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.index - b.index);
};

const runWhenIdle = (task) => {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const idleId = window.requestIdleCallback(task, { timeout: 250 });
    return () => window.cancelIdleCallback(idleId);
  }
  const timeoutId = window.setTimeout(task, 0);
  return () => window.clearTimeout(timeoutId);
};

const findLastWordArrayIndex = (words, time) => {
  let low = 0;
  let high = words.length - 1;
  let result = -1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (words[mid].start <= time) {
      result = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return result;
};

const clearActiveWordStyles = (wordIndex) => {
  if (wordIndex === -1) return;
  const old = document.getElementById(`word-${wordIndex}`);
  if (!old) return;
  old.style.removeProperty('font-weight');
  old.style.removeProperty('background-color');
  old.style.removeProperty('color');
  old.style.removeProperty('border-radius');
  old.style.removeProperty('padding');
};

const buildSpeakerBlocks = (words, diarizationSegments) => {
  if (!words.length) return [];
  if (!diarizationSegments?.length) return [{ speaker: null, words }];

  const segs = [...diarizationSegments].sort((a, b) => a.start - b.start);
  let segIdx = 0;

  const wordsWithSpeaker = words.map((word) => {
    const mid = (word.start + word.end) / 2;

    while (segIdx < segs.length - 1 && mid >= segs[segIdx].end) {
      segIdx += 1;
    }

    const currentSeg = segs[segIdx];
    let speaker = null;

    if (currentSeg && mid >= currentSeg.start && mid < currentSeg.end) {
      speaker = currentSeg.speaker;
    } else {
      const prevSeg = segs[Math.max(segIdx - 1, 0)];
      const nextSeg = segs[Math.min(segIdx, segs.length - 1)];
      const prevCenter = prevSeg ? (prevSeg.start + prevSeg.end) / 2 : Infinity;
      const nextCenter = nextSeg ? (nextSeg.start + nextSeg.end) / 2 : Infinity;
      const prevDistance = Math.abs(mid - prevCenter);
      const nextDistance = Math.abs(mid - nextCenter);
      speaker = (prevDistance <= nextDistance ? prevSeg : nextSeg)?.speaker ?? segs[0].speaker;
    }

    return { ...word, speaker };
  });

  const grouped = [];
  let currentSpeaker = wordsWithSpeaker[0].speaker;
  let currentWords = [];

  wordsWithSpeaker.forEach((word) => {
    if (word.speaker === currentSpeaker) {
      currentWords.push(word);
      return;
    }

    grouped.push({ speaker: currentSpeaker, words: currentWords });
    currentSpeaker = word.speaker;
    currentWords = [word];
  });

  grouped.push({ speaker: currentSpeaker, words: currentWords });
  return grouped;
};

const TranscriptWords = React.memo(({ words, onWordClick }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
    {words.map((w) => (
      <Typography
        key={w.index}
        id={`word-${w.index}`}
        onClick={() => onWordClick(w.start)}
        sx={{
          cursor: 'pointer',
          fontSize: { xs: '0.9rem', sm: '1rem' },
          marginRight: '3px',
          transition: 'all 0.1s ease'
        }}
      >
        {w.text}
      </Typography>
    ))}
  </Box>
));

const getSpeakerColor = (speakerId) => {
  if (speakerId === null) return '#757575';
  const colors = [
    '#1976d2', '#e53935', '#43a047', '#7b1fa2',
    '#ff9800', '#0097a7', '#c2185b', '#827717',
    '#3949ab', '#6d4c41',
  ];
  const hash = Math.abs(
    speakerId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  );
  return colors[hash % colors.length];
};

const getSpeakerInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const InteractiveTranscript = ({
  wordSRTUrl,
  currentTime,
  audioRef,
  isKaraokeEnabled,
  diarizationStatus,
  diarizationSegments,
  speakerNames,
  onUpdateSpeakerName,
  seekTrigger,
  recordingMetadata,
  onDownloadPDF,
  initialTimeOffsetSeconds = null,
}) => {
  const [words, setWords] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);

  const [localSpeakerNames, setLocalSpeakerNames] = useState(speakerNames || {});
  const [hoveredBlock, setHoveredBlock] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editedName, setEditedName] = useState('');
  const inputRefSpeaker = useRef(null);

  useEffect(() => {
    setLocalSpeakerNames(speakerNames || {});
  }, [speakerNames]);

  useEffect(() => {
    if (editingKey !== null && inputRefSpeaker.current) {
      inputRefSpeaker.current.select();
    }
  }, [editingKey]);

  useEffect(() => {
    if (seekTrigger > 0) {
      if (activeWordRef.current) {
        activeWordRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
      setAutoScroll(true);
    }
  }, [seekTrigger]);

  const startSpeakerEditing = (speakerId, blockIdx, currentName) => {
    setEditingKey(`${speakerId}-${blockIdx}`);
    setEditedName(currentName);
  };
  const cancelSpeakerEditing = () => {
    setEditingKey(null);
    setEditedName('');
  };
  const saveSpeaker = (speakerId) => {
    const trimmed = editedName.trim();
    if (!trimmed) return;
    const updated = { ...localSpeakerNames, [speakerId]: trimmed };
    setLocalSpeakerNames(updated);
    if (typeof onUpdateSpeakerName === 'function') {
      onUpdateSpeakerName(speakerId, trimmed);
    }
    setEditingKey(null);
  };
  const handleSpeakerKeyDown = (e, speakerId) => {
    if (e.key === 'Enter') {
      saveSpeaker(speakerId);
    } else if (e.key === 'Escape') {
      cancelSpeakerEditing();
    }
  };

  const prevActive = useRef(-1);
  const activeWordRef = useRef(null);

  useEffect(() => {
    if (!wordSRTUrl) {
      setWords([]);
      setBlocks([]);
      setErrorMsg('Չհաջողվեց բեռնել ենթագիր');
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    let cancelIdleParse = () => {};

    setIsLoading(true);
    setErrorMsg(null);

    fetch(wordSRTUrl)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.text();
      })
      .then((txt) => {
        cancelIdleParse = runWhenIdle(() => {
          if (!isMounted) return;
          const parsed = parseSRT(txt);
          if (!parsed.length) {
            setWords([]);
            setErrorMsg('Ենթագրերի ֆայլը դատարկ է');
          } else {
            setWords(parsed);
            setErrorMsg(null);
          }
          setIsLoading(false);
        });
      })
      .catch((err) => {
        if (!isMounted) return;
        setWords([]);
        setErrorMsg('Չհաջողվեց բեռնել ենթագիր');
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
      try {
        cancelIdleParse();
      } catch (cancelErr) {
        // Ignore idle callback cancellation errors during unmount.
      }
    };
  }, [wordSRTUrl]);

  useEffect(() => {
    if (!words.length) {
      setBlocks([]);
      return;
    }
    // If karaoke is OFF, always show plain text (no diarization)
    if (!isKaraokeEnabled || diarizationStatus !== 'succeeded' || !diarizationSegments?.length) {
      setBlocks([{ speaker: null, words }]);
      return;
    }
    setBlocks(buildSpeakerBlocks(words, diarizationSegments));
  }, [words, diarizationStatus, diarizationSegments, isKaraokeEnabled]);

  useEffect(() => {
    const onScroll = () => {
      setAutoScroll((prev) => (prev ? false : prev));
    };
    window.addEventListener('wheel', onScroll, { passive: true });
    window.addEventListener('touchmove', onScroll, { passive: true });
    return () => {
      window.removeEventListener('wheel', onScroll);
      window.removeEventListener('touchmove', onScroll);
    };
  }, []);

  useEffect(() => {
    if (!words.length) return;
    if (!isKaraokeEnabled) {
      if (prevActive.current !== -1) {
        clearActiveWordStyles(prevActive.current);
        prevActive.current = -1;
      }
      return;
    }
    const activeArrayIndex = findLastWordArrayIndex(words, currentTime);
    const nextActiveWordIndex = activeArrayIndex === -1 ? -1 : words[activeArrayIndex].index;

    if (nextActiveWordIndex !== prevActive.current) {
      if (prevActive.current !== -1) {
        clearActiveWordStyles(prevActive.current);
      }
      if (nextActiveWordIndex !== -1) {
        const el = document.getElementById(`word-${nextActiveWordIndex}`);
        if (el) {
          el.style.fontWeight = 'bold';
          el.style.backgroundColor = '#1976d2';
          el.style.color = 'white';
          el.style.borderRadius = '4px';
          el.style.padding = '0 4px';
          activeWordRef.current = el;
          if (autoScroll) {
            const rect = el.getBoundingClientRect();
            const topSafeZone = 120;
            const bottomSafeZone = window.innerHeight - 220;
            const isOutOfView = rect.top < topSafeZone || rect.bottom > bottomSafeZone;

            if (isOutOfView) {
              el.scrollIntoView({
                behavior: 'auto',
                block: 'center',
                inline: 'nearest'
              });
            }
          }
        }
      }
      prevActive.current = nextActiveWordIndex;
    }
  }, [currentTime, isKaraokeEnabled, words, autoScroll]);

  const handleWordClick = useCallback(
    (time) => {
      if (audioRef?.current) {
        audioRef.current.currentTime = time;
        audioRef.current.play().catch(() => { });
        setAutoScroll(true);
      }
    },
    [audioRef]
  );

  const formatTimestamp = (seconds) => formatTranscriptTimestamp(seconds, initialTimeOffsetSeconds);

  const copyAll = () => {
    const textToCopy = blocks
      .map((b, i) => {
        const name = b.speaker != null
          ? localSpeakerNames[b.speaker] || `Խոսող ${b.speaker + 1}`
          : `Խոսող ${i + 1}`;
        const timeStr = b.words && b.words.length > 0 ? formatTimestamp(b.words[0].start) + ' ' : '';
        const wordsText = b.words.map((w) => w.text).join(' ');
        return `${timeStr}${name}: ${wordsText}`;
      })
      .join('\n\n');

    const finalCopyText = textToCopy + `\n\nՁայնագրությունը տեքստ է դարձրել Xosum.am-ը։ \nՁայնագրության հղումը՝ ${window.location.href}`;

    navigator.clipboard
      .writeText(finalCopyText)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
  };

  const downloadPDF = () => {
    setPdfGenerating(true);

    const container = document.createElement('div');
    container.style.padding = '40px';
    container.style.fontFamily = 'Arial, sans-serif';
    container.style.color = '#333';
    container.style.lineHeight = '1.6';

    const header = document.createElement('div');
    header.style.textAlign = 'center';
    header.style.marginBottom = '30px';
    header.style.pageBreakInside = 'avoid';

    if (recordingMetadata?.title) {
      const titleEl = document.createElement('h1');
      titleEl.innerText = recordingMetadata.title;
      titleEl.style.fontSize = '24px';
      titleEl.style.marginBottom = '10px';
      header.appendChild(titleEl);
    }

    if (recordingMetadata?.createdAt) {
      const dateEl = document.createElement('p');
      const dateVal = typeof recordingMetadata.createdAt === 'object' && recordingMetadata.createdAt.toDate
        ? recordingMetadata.createdAt.toDate()
        : new Date(recordingMetadata.createdAt);

      const dateStr = !isNaN(dateVal.getTime()) ? dateVal.toLocaleDateString('hy-AM', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      }) : 'Անհայտ ամսաթիվ';

      dateEl.innerText = `Ամսաթիվ: ${dateStr}`;
      dateEl.style.margin = '5px 0';
      dateEl.style.color = '#666';
      header.appendChild(dateEl);
    }

    if (recordingMetadata?.source === 'upload' && recordingMetadata?.filePath) {
      const fileSplit = recordingMetadata.filePath.split('/');
      let fileName = fileSplit[fileSplit.length - 1] || recordingMetadata.filePath;
      const underscoreIdx = fileName.indexOf('_');
      if (underscoreIdx > -1) {
        fileName = fileName.substring(underscoreIdx + 1);
      }

      const fileEl = document.createElement('p');
      fileEl.innerText = `Ֆայլ: ${fileName}`;
      fileEl.style.margin = '5px 0';
      fileEl.style.color = '#666';
      header.appendChild(fileEl);
    }

    const allNames = Object.values(localSpeakerNames);
    if (allNames.length > 0) {
      const participantsEl = document.createElement('p');
      participantsEl.innerText = `Մասնակիցներ: ${allNames.join(', ')}`;
      participantsEl.style.margin = '5px 0';
      participantsEl.style.color = '#666';
      header.appendChild(participantsEl);
    }

    container.appendChild(header);

    container.appendChild(document.createElement('hr'));

    const content = document.createElement('div');
    content.style.marginTop = '20px';

    blocks.forEach((b, i) => {
      const name = b.speaker != null
        ? localSpeakerNames[b.speaker] || `Խոսող ${b.speaker + 1}`
        : `Խոսող ${i + 1}`;
      const timeStr = b.words && b.words.length > 0 ? formatTimestamp(b.words[0].start) + ' ' : '';
      const wordsText = b.words.map((w) => w.text).join(' ');

      const p = document.createElement('p');
      p.style.marginBottom = '15px';
      p.style.pageBreakInside = 'avoid';

      const timeSpan = document.createElement('span');
      timeSpan.innerText = timeStr;
      timeSpan.style.color = '#888';
      timeSpan.style.fontSize = '0.9em';
      p.appendChild(timeSpan);

      const nameSpan = document.createElement('strong');
      nameSpan.innerText = `${name}: `;
      p.appendChild(nameSpan);

      const textNode = document.createTextNode(wordsText);
      p.appendChild(textNode);

      content.appendChild(p);
    });

    container.appendChild(content);

    container.appendChild(document.createElement('hr'));

    const footer = document.createElement('div');
    footer.style.marginTop = '30px';
    footer.style.textAlign = 'center';
    footer.style.fontSize = '12px';
    footer.style.color = '#666';
    footer.style.display = 'flex';
    footer.style.flexDirection = 'column';
    footer.style.alignItems = 'center';
    footer.style.gap = '10px';
    footer.style.pageBreakInside = 'avoid';

    const logoBottom = document.createElement('img');
    logoBottom.src = '/logo/xosumAM_logo_transparent-min.png';
    logoBottom.style.width = '120px';
    footer.appendChild(logoBottom);

    const footerText = document.createElement('p');
    footerText.innerText = 'Ձայնագրությունը տեքստ է դարձրել Xosum.am-ը։';
    footerText.style.margin = '0';
    footer.appendChild(footerText);

    const linkEl = document.createElement('a');
    linkEl.href = window.location.href;
    linkEl.innerText = 'Ձայնագրության հղումը';
    linkEl.style.color = '#1976d2';
    linkEl.style.textDecoration = 'none';
    footer.appendChild(linkEl);

    container.appendChild(footer);

    let pdfFilename = `transcript-${recordingMetadata?.docId || 'download'}.pdf`;
    if (recordingMetadata?.title) {
      // Sanitize the title for filename usage
      const safeTitle = recordingMetadata.title.replace(/[^a-z0-9ա-ֆԱ-Ֆ\s-]/gi, '').trim().replace(/\s+/g, '_');
      if (safeTitle) {
        pdfFilename = `${safeTitle}.pdf`;
      }
    }

    const opt = {
      margin: 10,
      filename: pdfFilename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(container).save().then(() => {
      setPdfGenerating(false);
      if (onDownloadPDF) onDownloadPDF();
    }).catch(err => {
      console.error('Error generating PDF', err);
      setPdfGenerating(false);
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ mt: 2, p: 2, display: 'flex', justifyContent: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (errorMsg) {
    return (
      <Box sx={{ mt: 2, p: 2 }}>
        <Typography color="error">{errorMsg}</Typography>
      </Box>
    );
  }

  return (
    <Box
      onCopy={(e) => {
        e.preventDefault();
        e.clipboardData.setData(
          'text/plain',
          window
            .getSelection()
            .toString()
            .replace(/[\n\r]+/g, ' ')
            .replace(/\s+/g, ' ')
        );
      }}
      sx={{
        mt: 0,
        p: 0,
        mb: 1.5,
        position: 'relative'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 1.25,
          gap: { xs: 1, sm: 0 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Վերծանված տեքստ</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={downloadPDF}
            disabled={pdfGenerating}
            color="primary"
            size="small"
            sx={{ height: 'fit-content' }}
          >
            {pdfGenerating ? 'Ստեղծվում է...' : 'Ներբեռնել PDF'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={copyAll}
            color={copied ? 'success' : 'primary'}
            size="small"
            sx={{ height: 'fit-content' }}
          >
            {copied ? 'Պատճենված է' : 'Պատճենել'}
          </Button>
        </Box>
      </Box>

      {!isKaraokeEnabled ? (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: 'white',
            border: '1px solid',
            borderColor: 'divider',
            contentVisibility: 'auto',
            containIntrinsicSize: '480px'
          }}
        >
          <TranscriptWords words={words} onWordClick={handleWordClick} />
        </Paper>
      ) : (
        blocks.map((b, i) => {
          const defaultName =
            b.speaker != null
              ? localSpeakerNames[b.speaker] || `Խոսող ${b.speaker + 1}`
              : `Խոսող ${i + 1}`;
          const blockKey = `${b.speaker}-${i}`;
          const isEditingThis = editingKey === blockKey;
          const isHoveringThis = hoveredBlock === i;
          const speakerColor = getSpeakerColor(b.speaker);
          const speakerInitials = getSpeakerInitials(defaultName);

          return (
            <Box
              key={i}
              sx={{
                mb: 1.5,
                contentVisibility: 'auto',
                containIntrinsicSize: '220px'
              }}
            >
              {b.speaker != null ? (
                isEditingThis ? (
                  <Box sx={{ mb: 1 }}>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={editedName}
                      inputRef={inputRefSpeaker}
                      autoFocus
                      onChange={(e) => setEditedName(e.target.value)}
                      onKeyDown={(e) => handleSpeakerKeyDown(e, b.speaker)}
                      sx={{ mb: 1, width: { xs: '100%', sm: '300px' } }}
                      placeholder="Խոսնակի անուն"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: speakerColor
                              }}
                            >
                              {speakerInitials}
                            </Avatar>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => saveSpeaker(b.speaker)} size="small">
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton onClick={cancelSpeakerEditing} size="small">
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                      position: 'relative',
                      width: 'fit-content'
                    }}
                    onMouseEnter={() => setHoveredBlock(i)}
                    onMouseLeave={() => setHoveredBlock(null)}
                  >
                    <Chip
                      avatar={
                        <Avatar sx={{ bgcolor: speakerColor }}>
                          {speakerInitials}
                        </Avatar>
                      }
                      label={defaultName}
                      variant="filled"
                      onClick={() => startSpeakerEditing(b.speaker, i, defaultName)}
                      sx={{
                        fontWeight: 'bold',
                        pr: isHoveringThis ? 4 : 1,
                        transition: 'padding-right 0.2s',
                        position: 'relative',
                        cursor: 'pointer',
                        bgcolor: 'white',
                        border: 1,
                        borderColor: speakerColor,
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.9)'
                        }
                      }}
                    />
                    {isHoveringThis && (
                      <IconButton
                        onClick={() => startSpeakerEditing(b.speaker, i, defaultName)}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '100%',
                          ml: 1,
                          transform: 'translate(0, -50%)',
                          zIndex: 2,
                          bgcolor: 'white',
                          boxShadow: 1,
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.9)'
                          }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )
              ) : (
                <Box sx={{ mb: 1 }}>
                  <Chip
                    avatar={
                      <Avatar sx={{ bgcolor: 'grey.500' }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                    }
                    label={defaultName}
                    variant="filled"
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: 'white',
                      border: 1,
                      borderColor: 'grey.400'
                    }}
                  />
                </Box>
              )}
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: 'white',
                  borderLeft: 3,
                  borderColor: getSpeakerColor(b.speaker)
                }}
              >
                <TranscriptWords words={b.words} onWordClick={handleWordClick} />
              </Paper>
            </Box>
          );
        })
      )}

    </Box>
  );
};

export default InteractiveTranscript;
